const ERROR_MESSAGE_MAP = {
  // Network errors
  'Network request failed':
    'Unable to connect to server. Please check your internet connection.',
  'Failed to fetch':
    'Network error. Please check your connection and try again.',
  'TypeError: Failed to fetch':
    'Network error. Please check your connection and try again.',

  // Authentication errors
  'Invalid Email or password.': 'Invalid email or password. Please try again.',
  'Authentication failed': 'Your session has expired. Please log in again.',

  // Validation errors
  'Record not found': 'The requested item could not be found.',
  'Parameter missing':
    'Required information is missing. Please fill in all required fields.',
  'Unverified request':
    'Security verification failed. Please refresh the page and try again.',

  // Form validation patterns
  "can't be blank": 'This field is required.',
  'is not a valid email': 'Please enter a valid email address.',
  'is too short': 'This field is too short.',
  'is too long': 'This field is too long.',
  'has already been taken': 'This value is already in use.',

  // Payment errors
  'Your card was declined':
    'Your payment method was declined. Please check your card details or try a different payment method.',
  'Payment failed':
    'Payment could not be processed. Please try again or use a different payment method.',
}

export const getUserFriendlyErrorMessage = (
  error,
  fallback = 'An unexpected error occurred. Please try again.'
) => {
  if (!error) return fallback

  const errorMessage =
    typeof error === 'string' ? error : error.message || error.error || ''

  if (ERROR_MESSAGE_MAP[errorMessage]) {
    return ERROR_MESSAGE_MAP[errorMessage]
  }

  for (const [pattern, message] of Object.entries(ERROR_MESSAGE_MAP)) {
    if (errorMessage.toLowerCase().includes(pattern.toLowerCase())) {
      return message
    }
  }

  if (!containsTechnicalTerms(errorMessage)) {
    return errorMessage
  }

  return fallback
}

export const processValidationErrors = errors => {
  if (!errors || typeof errors !== 'object') return errors

  const processedErrors = {}

  for (const [field, messages] of Object.entries(errors)) {
    if (Array.isArray(messages)) {
      processedErrors[field] = messages.map(msg =>
        getUserFriendlyErrorMessage(msg, msg)
      )
    } else {
      processedErrors[field] = getUserFriendlyErrorMessage(messages, messages)
    }
  }

  return processedErrors
}

const containsTechnicalTerms = message => {
  const technicalTerms = [
    'undefined',
    'null',
    'NaN',
    'TypeError',
    'ReferenceError',
    'SyntaxError',
    'fetch',
    'xhr',
    'ajax',
    'status 500',
    'status 404',
    'Internal Server Error',
    'Bad Gateway',
    'Service Unavailable',
  ]

  return technicalTerms.some(term =>
    message.toLowerCase().includes(term.toLowerCase())
  )
}

export const getContextualErrorMessage = (error, context) => {
  const baseMessage = getUserFriendlyErrorMessage(error)

  const contextualMessages = {
    patient: {
      not_found:
        'Patient not found. Please check the patient ID and try again.',
      create_failed:
        'Unable to create patient. Please check the required fields.',
      update_failed: 'Unable to update patient information. Please try again.',
    },
    appointment: {
      not_found:
        'Appointment not found. It may have been cancelled or rescheduled.',
      create_failed:
        'Unable to schedule appointment. Please check the selected time slot.',
      update_failed: 'Unable to update appointment. Please try again.',
      conflict:
        'This time slot is no longer available. Please choose a different time.',
    },
    payment: {
      declined:
        'Payment was declined. Please check your payment details or try a different method.',
      failed:
        'Payment could not be processed. Please contact support if this continues.',
    },
    upload: {
      failed:
        'File upload failed. Please check your file size and format, then try again.',
      too_large: 'File is too large. Please choose a smaller file.',
      invalid_format:
        'Invalid file format. Please choose a supported file type.',
    },
  }

  if (context && contextualMessages[context]) {
    // Try to match specific error patterns for this context
    for (const [pattern, message] of Object.entries(
      contextualMessages[context]
    )) {
      if (error && error.toLowerCase().includes(pattern.replace('_', ' '))) {
        return message
      }
    }
  }

  return baseMessage
}

export const parseClaimValidationError = message => {
  if (!message || typeof message !== 'string') return message

  const match = message.match(/claim\.([^:]+):\s*(.+)/)

  if (!match) return message

  const [, fieldPath, errorDescription] = match
  const parts = fieldPath.split('.')

  const humanReadableField = parts
    .map((part, index) => {
      const formatted = part
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim()
      return formatted
    })
    .join(' ')

  let status = ''
  let fieldMessage = ''

  if (
    errorDescription.includes('Invalid type') &&
    errorDescription.includes('null')
  ) {
    status = 'Missing'
    fieldMessage = humanReadableField
  } else if (errorDescription.includes('String length must be greater than')) {
    status = 'Required'
    fieldMessage = humanReadableField
  } else if (errorDescription.includes('Invalid type')) {
    const typeMatch = errorDescription.match(/Expected:\s*(\w+)/)
    if (typeMatch) {
      status = 'Invalid Type'
      fieldMessage = `${humanReadableField} (expected ${typeMatch[1]})`
    } else {
      status = 'Invalid Format'
      fieldMessage = humanReadableField
    }
  } else if (errorDescription.includes('Required')) {
    status = 'Required'
    fieldMessage = humanReadableField
  } else if (errorDescription.includes('must be')) {
    status = 'Validation Error'
    fieldMessage = `${humanReadableField} ${errorDescription.toLowerCase()}`
  } else {
    status = 'Error'
    fieldMessage = `${humanReadableField} - ${errorDescription}`
  }

  return `${status}: ${fieldMessage}`
}

export const parseClaimValidationErrors = messages => {
  if (!Array.isArray(messages)) return []

  return messages.map(msg => parseClaimValidationError(msg)).filter(Boolean)
}
